/**
 * What the Beats home says, worked out from the beat list.
 *
 * The two questions this product's home answers are "what needs me" and "is the
 * watch running" — and the second one has no answer anywhere in the app today.
 * A beat whose scheduler quietly stopped looks exactly like a beat with nothing
 * to report: the list shows a name, a cadence, and a last briefing that is a bit
 * old. THAT is the failure a standing watch has, and it is invisible.
 *
 * Everything here comes from `NewsBeatSummary`, which the list endpoint already
 * returns. No new API.
 *
 * Pure and dependency-free so CRA's jest can test it.
 *
 * NAMED FOR WHAT IT COMPUTES, not for the screen that shows it: `beatsHome.ts`
 * beside `BeatsHome.tsx` differs only in casing, which macOS cannot tell apart
 * and TypeScript refuses. Same convention as `deliverTabs.ts` and
 * `trendFreshness.ts`.
 */
import type { NewsBeatSummary } from '../types';
import { isOverdue, nextRunAt } from './cadence';

export type BeatProblem = 'DRAFT' | 'FAILED' | 'OVERDUE';

export interface BeatNeedingYou {
  beat: NewsBeatSummary;
  problem: BeatProblem;
  /**
   * What is wrong, in the reader's terms.
   *
   * NOT THE INTERNAL STATE'S NAME. "Still a draft — it is not watching yet"
   * named a mode the reader has no reason to know and left every real question
   * open: not watching WHAT, is that deliberate, and what would change it. Each
   * of these now says the consequence rather than the state.
   */
  detail: string;
  /** The single thing to do about it, naming the control that does it. */
  fix: string;
  /** A short label for the row. Plain words, not the enum. */
  label: string;
}

/**
 * Beats that are not doing their job, worst first.
 *
 * ORDER IS BY HOW BROKEN, not by name or date. A failed run and a beat that
 * never went live are different kinds of stuck, and the one that was running
 * and stopped is the one to look at first.
 */
export function beatsNeedingYou(
  beats: NewsBeatSummary[],
  now: Date = new Date(),
): BeatNeedingYou[] {
  const out: BeatNeedingYou[] = [];

  for (const beat of beats) {
    // A live beat that has missed a whole cycle. First, because it is the only
    // one of these that was working and is not any more.
    if (beat.is_active && isOverdue(beat.last_run_at, beat.interval_minutes, now)) {
      out.push({
        beat,
        problem: 'OVERDUE',
        label: 'Behind schedule',
        detail: 'It was due a briefing and did not produce one',
        fix: 'Open it and run a briefing by hand - if that works, the scheduler is the problem.',
      });
      continue;
    }
    if (beat.last_briefing_status === 'FAILED') {
      out.push({
        beat,
        problem: 'FAILED',
        label: 'Last run failed',
        detail: 'Its most recent briefing ended in an error',
        fix: 'Open it to read the error on that briefing.',
      });
      continue;
    }
    // Built and never taken live. Not broken, but it is doing nothing and the
    // person who made it probably thinks otherwise.
    if (beat.mode === 'DRAFT') {
      out.push({
        beat,
        problem: 'DRAFT',
        label: 'Not started',
        // Says the CONSEQUENCE. "Still a draft" is the mode's name, and the
        // mode is our word for something the reader only cares about in one
        // respect: this beat is finished but nothing is running it.
        detail: 'Never gone live, so it will never run on its own',
        fix: 'Open it and press Go live. That clears what the draft produced and starts the schedule.',
      });
    }
  }

  const rank: Record<BeatProblem, number> = { OVERDUE: 0, FAILED: 1, DRAFT: 2 };
  return out.sort((a, b) => rank[a.problem] - rank[b.problem]);
}

export interface WatchSummary {
  live: number;
  draft: number;
  paused: number;
  /** The soonest run due across every live beat, or null if none can be dated. */
  nextDue: Date | null;
  /** Stories in the most recent briefing of each live beat, summed. */
  storiesLastRound: number;
}

/** The state of the watch as a whole — the "when do I next hear something"
 *  answer, which is the question a cadence product exists to answer. */
export function watchSummary(
  beats: NewsBeatSummary[],
  now: Date = new Date(),
): WatchSummary {
  let live = 0;
  let draft = 0;
  let paused = 0;
  let storiesLastRound = 0;
  let nextDue: Date | null = null;

  for (const beat of beats) {
    if (beat.mode === 'DRAFT') draft += 1;
    else if (!beat.is_active) paused += 1;
    else {
      live += 1;
      storiesLastRound += beat.last_briefing_stories || 0;

      const due = nextRunAt(beat.last_run_at, beat.interval_minutes);
      // Only future runs: a beat that is overdue is reported as overdue, not as
      // "next due two days ago", which reads like a broken clock rather than a
      // broken beat.
      if (due && due.getTime() >= now.getTime()) {
        if (!nextDue || due.getTime() < nextDue.getTime()) nextDue = due;
      }
    }
  }

  return { live, draft, paused, nextDue, storiesLastRound };
}

/**
 * The most recent briefings worth showing, newest first.
 *
 * `exclude` is for beats already surfaced above as needing attention. A beat
 * listed under "needs you" and again under "lately" is the same row twice on a
 * short screen, and with three beats — where two are stuck — the home reads as
 * if it is repeating itself rather than telling you two different things.
 */
export function recentBriefings(
  beats: NewsBeatSummary[],
  limit = 5,
  exclude: Set<number> = new Set(),
): NewsBeatSummary[] {
  return beats
    .filter((b) => !exclude.has(b.id))
    .filter((b) => b.last_briefing_id && b.last_briefing_at)
    .sort(
      (a, b) =>
        new Date(b.last_briefing_at!).getTime() - new Date(a.last_briefing_at!).getTime(),
    )
    .slice(0, limit);
}
